import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/shared/lib/supabase'
import type { Feature, FeatureWithActions, FeatureActionCounts } from '@/shared/types'

interface FeatureWithUS extends Feature {
  user_stories: { id: string; status: string }[]
}

function calculateActionCounts(userStories: { id: string; status: string }[]): FeatureActionCounts {
  return {
    pendingValidation: userStories.filter(us => us.status === 'pending_validation').length,
    testing: userStories.filter(us => us.status === 'testing').length,
    totalUS: userStories.length,
  }
}

async function fetchFeatures(projectId: string): Promise<FeatureWithActions[]> {
  const { data, error } = await supabase
    .from('features')
    .select(`
      *,
      user_stories (id, status)
    `)
    .eq('project_id', projectId)
    .order('column_order', { ascending: true })

  if (error) {
    throw new Error(error.message)
  }

  // Transform to FeatureWithActions
  return (data || []).map((feature: FeatureWithUS) => ({
    ...feature,
    actionCounts: calculateActionCounts(feature.user_stories || []),
  }))
}

export function useFeatures(projectId: string | undefined) {
  return useQuery({
    queryKey: ['features', projectId],
    queryFn: () => fetchFeatures(projectId!),
    enabled: !!projectId,
  })
}

async function fetchFeatureByShortId(shortId: string): Promise<FeatureWithActions | null> {
  const { data, error } = await supabase
    .from('features')
    .select(`
      *,
      user_stories (id, status)
    `)
    .eq('short_id', shortId)
    .single()

  if (error) {
    if (error.code === 'PGRST116') {
      return null
    }
    throw new Error(error.message)
  }

  const feature = data as FeatureWithUS
  return {
    ...feature,
    actionCounts: calculateActionCounts(feature.user_stories || []),
  }
}

export function useFeatureByShortId(shortId: string | undefined) {
  return useQuery({
    queryKey: ['feature', 'short', shortId],
    queryFn: () => fetchFeatureByShortId(shortId!),
    enabled: !!shortId,
  })
}
